"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useMenu = exports.findNextEnabledIndex = void 0;
var react_1 = __importStar(require("react"));
var findNextEnabledIndex = function (_a) {
    var totalItems = _a.totalItems, _b = _a.isItemDisabled, isItemDisabled = _b === void 0 ? function () { return false; } : _b, currentIndex = _a.currentIndex, direction = _a.direction;
    // if all items are disabled, return the current index
    if (Array(totalItems)
        .fill(1)
        .every(function (_item, index) { return isItemDisabled(index); })) {
        return currentIndex;
    }
    var indexChange = direction === 'next' ? 1 : -1;
    var nextEnabledIndex = currentIndex + indexChange;
    // wrap the next index around the array both ways
    if (nextEnabledIndex < 0) {
        nextEnabledIndex = totalItems - 1;
    }
    if (nextEnabledIndex === totalItems) {
        nextEnabledIndex = 0;
    }
    if (isItemDisabled(nextEnabledIndex)) {
        return (0, exports.findNextEnabledIndex)({
            totalItems: totalItems,
            isItemDisabled: isItemDisabled,
            currentIndex: nextEnabledIndex,
            direction: direction,
        });
    }
    return nextEnabledIndex;
};
exports.findNextEnabledIndex = findNextEnabledIndex;
/** A hook that returns correct accessibility props and keyboard controls for a popover menu.  Based on this article:
 * https://www.smashingmagazine.com/2017/11/building-accessible-menu-systems/#keyboard-and-focus-behavior
 */
var useMenu = function (_a) {
    var isOpen = _a.isOpen, setIsOpen = _a.setIsOpen, isItemDisabled = _a.isItemDisabled, totalItems = _a.totalItems;
    var menuItemRefs = (0, react_1.useRef)(Array.from(Array(totalItems), function () { return react_1.default.createRef(); }));
    var _b = react_1.default.useState(function () {
        return (0, exports.findNextEnabledIndex)({
            totalItems: totalItems,
            isItemDisabled: isItemDisabled,
            currentIndex: -1,
            direction: 'next',
        });
    }), highlightedIndex = _b[0], setHighlightedIndex = _b[1];
    // focus on highlighted item
    (0, react_1.useEffect)(function () {
        var _a, _b, _c, _d;
        if (((_a = menuItemRefs.current) === null || _a === void 0 ? void 0 : _a[highlightedIndex]) && isOpen) {
            (_d = (_c = (_b = menuItemRefs.current) === null || _b === void 0 ? void 0 : _b[highlightedIndex]) === null || _c === void 0 ? void 0 : _c.current) === null || _d === void 0 ? void 0 : _d.focus({ preventScroll: true });
        }
    }, [isOpen, menuItemRefs, highlightedIndex]);
    var handleKeyDown = function (event) {
        event.preventDefault();
        // enter is handled by the onClick on the menuitem
        if (event.code === 'ArrowDown') {
            setHighlightedIndex(function (index) {
                var nextIndex = (0, exports.findNextEnabledIndex)({ totalItems: totalItems, isItemDisabled: isItemDisabled, currentIndex: index, direction: 'next' });
                return nextIndex;
            });
        }
        if (event.code === 'ArrowUp') {
            setHighlightedIndex(function (index) {
                var previousIndex = (0, exports.findNextEnabledIndex)({
                    totalItems: totalItems,
                    isItemDisabled: isItemDisabled,
                    currentIndex: index,
                    direction: 'previous',
                });
                return previousIndex;
            });
        }
    };
    var handleOpenWithItemHighlighted = function (item) {
        setIsOpen(true);
        var firstIndex = (0, exports.findNextEnabledIndex)({
            totalItems: totalItems,
            isItemDisabled: isItemDisabled,
            currentIndex: item === 'first' ? -1 : totalItems,
            direction: item === 'first' ? 'next' : 'previous',
        });
        setHighlightedIndex(firstIndex);
    };
    return {
        getMenuProps: function (props) {
            var onKeyDown = (props !== null && props !== void 0 ? props : {}).onKeyDown;
            return {
                onKeyDown: function (event) {
                    handleKeyDown(event);
                    onKeyDown === null || onKeyDown === void 0 ? void 0 : onKeyDown(event);
                },
                role: 'menu',
                'aria-orientation': 'vertical',
            };
        },
        getTriggerProps: function () { return ({
            onKeyDown: function (event) {
                if (event.code === 'ArrowDown' || event.code === 'Enter' || event.code === 'Space') {
                    event.preventDefault();
                    handleOpenWithItemHighlighted('first');
                }
                if (event.code === 'ArrowUp') {
                    event.preventDefault();
                    handleOpenWithItemHighlighted('last');
                }
            },
            onClick: function () {
                if (!isOpen) {
                    handleOpenWithItemHighlighted('first');
                }
                else {
                    setIsOpen(false);
                }
            },
        }); },
        getItemProps: function (index, _a) {
            var _b;
            var onClick = _a.onClick, onKeyDown = _a.onKeyDown;
            return !isItemDisabled(index)
                ? {
                    ref: (_b = menuItemRefs === null || menuItemRefs === void 0 ? void 0 : menuItemRefs.current) === null || _b === void 0 ? void 0 : _b[index],
                    role: 'menuitem',
                    tabIndex: highlightedIndex === index ? 0 : -1,
                    onClick: function (event) {
                        setIsOpen(false);
                        onClick === null || onClick === void 0 ? void 0 : onClick(event);
                    },
                    isHighlighted: index === highlightedIndex,
                    onMouseOver: function () { return setHighlightedIndex(index); },
                    onKeyDown: function (event) {
                        var _a;
                        if (event.code === 'Enter' || event.code === 'Space') {
                            (_a = event === null || event === void 0 ? void 0 : event.currentTarget) === null || _a === void 0 ? void 0 : _a.click();
                        }
                        if (onKeyDown) {
                            onKeyDown(event);
                        }
                    },
                }
                : { tabIndex: -1 };
        },
        highlightedIndex: highlightedIndex,
    };
};
exports.useMenu = useMenu;
